import type { Components, Theme } from "@mui/material/styles";
import { designShadows } from "../../tokens/shadows";
import { RadiusToken } from "../../tokens/radii";

// MuiOutlinedInput already styles the bordered shell, focus shadow, and per-size
// input padding. Autocomplete then *overrides* that padding internally — it moves
// padding from the <input> onto the inputRoot so tags can wrap inline. These
// overrides restore our spacing tokens at the new layer and add the popup
// surfaces (paper, listbox, option, tag) that OutlinedInput doesn't cover.
export const MuiAutocomplete: NonNullable<
  Components<Theme>["MuiAutocomplete"]
> = {
  defaultProps: { size: "medium" },
  styleOverrides: {
    // Neutralize Autocomplete's default `9px` inputRoot padding and the
    // `7.5px 4px 7.5px 6px` override on the inner input. Real padding is
    // applied by the size variants below so heights match standalone TextField.
    inputRoot: {
      padding: 0,
      "& .MuiAutocomplete-input": {
        padding: 0,
      },
    },
    paper: ({ theme }) => ({
      borderRadius: RadiusToken.MD,
      boxShadow: designShadows[4],
      backgroundColor: theme.vars!.palette.rmh.paper["1-surface"].rest,
    }),
    listbox: ({ theme }) => ({
      padding: theme.spacing(1),
      ...theme.typography.bodyMdRegular,
    }),
    option: ({ theme }) => {
      const rmh = theme.vars!.palette.rmh;
      return {
        ...theme.typography.bodyMdRegular,
        borderRadius: RadiusToken.XS,
        padding: `${theme.spacing(2)} ${theme.spacing(3)}`,
        "&.Mui-focused": {
          backgroundColor: rmh.paper["4-faint"].hover,
        },
        '&[aria-selected="true"]': {
          backgroundColor: rmh.paper["7-brandFaint"].rest,
        },
        '&[aria-selected="true"].Mui-focused': {
          backgroundColor: rmh.paper["7-brandFaint"].hover,
        },
      };
    },
    tag: ({ theme }) => ({
      margin: theme.spacing(0.5),
    }),
  },
  variants: [
    {
      props: { size: "small" },
      style: ({ theme }) => ({
        "& .MuiAutocomplete-inputRoot": {
          padding: `${theme.spacing(1.5)} ${theme.spacing(3)}`,
          gap: theme.spacing(1),
        },
      }),
    },
    {
      props: { size: "medium" },
      style: ({ theme }) => ({
        "& .MuiAutocomplete-inputRoot": {
          padding: `${theme.spacing(2.5)} ${theme.spacing(3)}`,
          gap: theme.spacing(1),
        },
      }),
    },
  ],
};
